import { useState } from 'react';
import { useApi } from '../../api/ApiProvider';
import { useApiQuery } from '../../api/useApiQuery';
import type { DocumentTypeAdmin } from '../../api/types';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Chip } from '../../components/ui/Chip';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { Spinner } from '../../components/ui/Spinner';

const MB = 1024 * 1024;

function Row({ dt, onSaved }: { dt: DocumentTypeAdmin; onSaved: () => void }) {
  const api = useApi();
  const [maxMb, setMaxMb] = useState(String(Math.round(dt.maxBytes / MB)));
  const [label, setLabel] = useState(dt.label);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const dirty = label !== dt.label || Number(maxMb) !== Math.round(dt.maxBytes / MB);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.admin.updateDocumentType(dt.code, { label, maxBytes: Math.max(1, Number(maxMb)) * MB });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className="gsl-row border-b border-border align-top last:border-b-0">
      <td className="px-4 py-2">
        <code className="text-xs">{dt.code}</code>
        <div className="mt-1 flex gap-1">
          <Chip variant="neutral">{dt.entityType}</Chip>
          {dt.sensitivity === 'restricted' ? <Chip variant="warning">restricted</Chip> : null}
          {dt.immutable ? <Chip variant="danger">WORM</Chip> : null}
        </div>
      </td>
      <td className="px-4 py-2">
        <input className="gsl-input !py-1 text-sm" value={label} onChange={(e) => setLabel(e.target.value)} aria-label={`Label for ${dt.code}`} />
      </td>
      <td className="px-4 py-2 text-xs">{dt.allowedExt.map((e) => e.toUpperCase()).join(', ')}</td>
      <td className="px-4 py-2">
        <input
          type="number"
          min={1}
          className="gsl-input w-24 !py-1 text-sm"
          value={maxMb}
          onChange={(e) => setMaxMb(e.target.value)}
          aria-label={`Maximum size in MB for ${dt.code}`}
        />
      </td>
      <td className="px-4 py-2 text-xs">
        {dt.retentionYears === null ? 'none' : `${dt.retentionYears}y`} · {dt.retentionTrigger.replace(/_/g, ' ')}
      </td>
      <td className="px-4 py-2 text-right">
        <Button size="sm" variant="secondary" disabled={!dirty} busy={busy} onClick={() => void save()}>
          Save
        </Button>
        {error ? <p className="mt-1 text-xs text-destructive">{error.message}</p> : null}
      </td>
    </tr>
  );
}

export function DocumentTypesAdmin() {
  const query = useApiQuery((api, signal) => api.admin.documentTypes(signal), []);
  return (
    <Card title="Document types" padded={false}>
      <p className="px-4 pt-3 text-xs text-muted-foreground">
        Policy is data: labels and size limits can be adjusted here. Allowed formats, sensitivity, immutability and
        retention periods are changed in code (and, for retention, only after counsel signs off).
      </p>
      {query.loading ? (
        <div className="p-4">
          <Spinner label="Loading document types" />
        </div>
      ) : query.error ? (
        <div className="p-4">
          <ErrorBanner error={query.error} />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="gsl-th">Code</th>
                <th className="gsl-th">Label</th>
                <th className="gsl-th">Formats</th>
                <th className="gsl-th">Max MB</th>
                <th className="gsl-th">Retention</th>
                <th className="gsl-th"></th>
              </tr>
            </thead>
            <tbody>
              {(query.data ?? []).map((dt) => (
                <Row key={dt.code} dt={dt} onSaved={query.reload} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
